import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Menu, X, ChevronRight, Check, Users, Award, ArrowRight,
  TrendingUp, Shield, Building2, UserCog, Layers, BarChart3,
  Brain, Target, Star, Eye, Zap, Globe, Maximize2, MessageSquare,
  Calendar, Video, Trophy, Flag, Sparkles, FileText, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const SaaSLandingPage = () => {
  // Tailwind JIT safelist (referenced dynamically via bg-${color}/10 etc.):
  // bg-orange-400/10 border-orange-400/20 border-orange-400/30 text-orange-400 bg-orange-400/5 bg-orange-400/15 hover:bg-orange-400/10 hover:bg-orange-400/15
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, alt }

  // Lock body scroll when lightbox is open + close on Esc
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [lightbox]);

  const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white overflow-hidden relative">
      {/* ========== BRAND WATERMARK (fixed background) ========== */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center"
      >
        <img
          src="/brand/meybra-logo.png"
          alt=""
          className="w-[120vmin] max-w-none opacity-[0.035] select-none"
          style={{ filter: 'blur(0.5px)' }}
        />
      </div>

      {/* ========== NAVBAR ========== */}
      <nav className="fixed top-0 w-full z-50 bg-[#0a0a0b]/90 backdrop-blur-sm border-b border-border/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/brand/meybra-logo.png" alt="Meybra" className="h-9 w-auto" />
            <span className="font-heading text-2xl tracking-wider text-white uppercase">MEYBRA</span>
            <span className="text-xs text-muted-foreground hidden lg:block ml-2 border-l border-border/30 pl-3">Soccer Development Platform</span>
          </Link>

          <div className="hidden md:flex items-center gap-8 text-sm uppercase tracking-widest text-muted-foreground">
            <a href="#how-it-works" className="hover:text-primary transition-colors">Features</a>
            <a href="#for-clubs" className="hover:text-primary transition-colors">For Clubs</a>
            <a href="#pricing" className="hover:text-primary transition-colors">Pricing</a>
            <Link to="/unified-login" className="hover:text-primary transition-colors" data-testid="login-link">Login</Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/get-started">
              <Button className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider text-sm font-bold" data-testid="header-start-free-btn">
                Join Founding Beta
              </Button>
            </Link>
          </div>

          <button className="md:hidden text-muted-foreground" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-[#0a0a0b] border-t border-border/10 px-4 py-6 space-y-4"
          >
            <a href="#how-it-works" className="block text-sm uppercase tracking-widest text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#for-clubs" className="block text-sm uppercase tracking-widest text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>For Clubs</a>
            <a href="#pricing" className="block text-sm uppercase tracking-widest text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <Link to="/unified-login" className="block text-sm uppercase tracking-widest text-muted-foreground">Login</Link>
            <hr className="border-border/20" />
            <Link to="/get-started"><Button className="w-full bg-primary text-black uppercase tracking-wider font-bold">Join Founding Beta</Button></Link>
          </motion.div>
        )}
      </nav>

      {/* ========== HERO ========== */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/3 via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-6">
              <Sparkles size={11} className="text-primary" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary">Founding Beta Access · Now Open</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase leading-[1.1] tracking-wide">
              Build better players with <br className="hidden sm:block" />
              <span className="text-primary">real development visibility</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Meybra connects coaches, clubs, players and families in one development system — whether an athlete trains privately, through a club, or both.
            </p>
            <p className="mt-3 text-sm text-zinc-500">
              Early clubs, coaches, and families are already helping shape Meybra during the Founding Beta.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/get-started" data-testid="hero-start-free-btn">
              <Button className="bg-primary text-black hover:bg-primary/90 px-8 py-3 text-sm font-semibold uppercase tracking-wider h-auto">
                Join Founding Beta <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/coach/signup" data-testid="hero-coach-btn">
              <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 px-8 py-3 text-sm font-semibold uppercase tracking-wider h-auto">
                Explore Private Coaching
              </Button>
            </Link>
          </motion.div>

          {/* Trust badges */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-14 flex flex-wrap items-center justify-center gap-6 text-[10px] uppercase tracking-widest text-zinc-600"
          >
            <span className="flex items-center gap-1.5"><Shield size={12} className="text-primary/50" /> Secure Platform</span>
            <span className="flex items-center gap-1.5"><Globe size={12} className="text-primary/50" /> Multi-Team Ready</span>
            <span className="flex items-center gap-1.5"><Brain size={12} className="text-primary/50" /> AI-Powered Insights</span>
          </motion.div>
        </div>
      </section>

      {/* ========== CHOOSE HOW YOU USE MEYBRA ========== */}
      <section id="how-it-works" className="py-20 px-4 border-t border-border/10">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
              Choose how you use <span className="text-primary">Meybra</span>
            </h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-lg mx-auto">Multiple entry points. One unified development ecosystem.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: UserCog, title: 'Private Soccer Coaches', color: 'primary',
                tagline: 'Build your coaching business with structured development.',
                signupHref: '/coach/signup',
                signupCta: 'Sign up as Soccer Coach',
                groups: [
                  {
                    label: 'Athlete Management',
                    items: ['Invite athletes & families', 'Athlete profiles & history', 'Position-aware evaluations', 'Skills radar & development timeline'],
                  },
                  {
                    label: 'Sessions & Plans',
                    items: ['Training plans builder', 'Drill library (60+ drills)', 'Calendar & session scheduling', 'Video analysis & feedback'],
                  },
                  {
                    label: 'Business',
                    items: ['Athlete Pass program', 'Financials & revenue tracking', 'Vouchers & packages (Pro)', 'Messages with parents'],
                  },
                ],
              },
              {
                icon: Activity, title: 'Performance Coaches', color: 'orange-400',
                tagline: 'Physical specialist — own the athlete\u2019s explosiveness.',
                signupHref: '/coach/signup?role=performance',
                signupCta: 'Sign up as Performance Coach',
                groups: [
                  {
                    label: 'Physical Evaluation',
                    items: ['7-attribute physical suite', 'Dedicated Physical Radar', 'AI-generated physical narratives', 'Contributes to the unified athlete profile'],
                  },
                  {
                    label: 'Programming',
                    items: ['Strength & conditioning plans', 'Speed, agility, explosiveness drills', 'Session calendar & history', 'Video feedback on movement'],
                  },
                  {
                    label: 'Business',
                    items: ['Athlete Pass program', 'Direct messaging with parents', 'Financials dashboard', 'Works alongside Soccer Coach'],
                  },
                ],
              },
              {
                icon: Users, title: 'Players & Families', color: 'emerald-400',
                tagline: 'Real visibility — beyond attendance.',
                signupHref: '/athlete/signup',
                signupCta: 'Create Athlete Account',
                groups: [
                  {
                    label: 'Development Visibility',
                    items: ['AI-powered Development Profile', 'Skills radar across 7 areas', 'Progression timeline (overall, technical, physical, tactical)', 'Personalized goals tracking'],
                  },
                  {
                    label: 'Training & Content',
                    items: ['Train at Home guided sessions', 'Technical Dev exercises', 'Personalized training plan', 'Video feedback library'],
                  },
                  {
                    label: 'Stay Connected',
                    items: ['Direct messages with coaches', 'Sessions calendar', 'Monthly progress reports', 'Multi-coach visibility (Private + Club)'],
                  },
                ],
              },
              {
                icon: Building2, title: 'Clubs', color: 'blue-400',
                tagline: 'Operate your academy at scale with intelligence.',
                signupHref: '/club-pilot',
                signupCta: 'Request Club Pilot',
                groups: [
                  {
                    label: 'Squad Operations',
                    items: ['Multi-team management (U8 → Senior)', 'Team Overviews (record, squad, matches)', 'Coaches & Managers role assignment', 'Athletes roster & invites'],
                  },
                  {
                    label: 'Match System',
                    items: ['Match scheduling & reports', 'Position-aware match scoring', 'Match Report full-edit with auto-recalc', 'Match history & insights'],
                  },
                  {
                    label: 'Decision Intelligence',
                    items: ['Performance Intelligence dashboard', 'Global filters (season, team, age, date)', 'AI Decision Engine (promote / develop / focus)', 'Training vs Match gap analysis', 'Critical insights & recommended actions'],
                  },
                ],
              },
            ].map((card, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-card border border-border/30 rounded-sm p-6 hover:border-primary/20 transition-all flex flex-col"
                data-testid={`persona-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-12 h-12 rounded-sm bg-${card.color}/10 border border-${card.color}/20 flex items-center justify-center mb-5`}>
                  <card.icon size={22} className={`text-${card.color}`} />
                </div>
                <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{card.title}</h3>
                <p className="text-xs text-muted-foreground mb-6 leading-relaxed">{card.tagline}</p>

                <div className="space-y-5 flex-1">
                  {card.groups.map((group, gi) => (
                    <div key={gi}>
                      <p className={`text-[10px] uppercase tracking-[0.18em] text-${card.color} mb-2.5 font-semibold`}>
                        {group.label}
                      </p>
                      <ul className="space-y-1.5">
                        {group.items.map((f, fi) => (
                          <li key={fi} className="flex items-start gap-2 text-[13px] text-muted-foreground leading-relaxed">
                            <Check size={12} className={`text-${card.color} shrink-0 mt-1`} /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {card.signupHref && (
                  <Link
                    to={card.signupHref}
                    className={`mt-6 flex items-center justify-between gap-2 px-3 py-2.5 rounded-sm border border-${card.color}/30 bg-${card.color}/5 hover:bg-${card.color}/10 transition-colors text-[11px] uppercase tracking-widest font-semibold text-${card.color}`}
                    data-testid={`persona-cta-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span>{card.signupCta}</span>
                    <ArrowRight size={13} />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CLUB DASHBOARD SECTION ========== */}
      <section id="for-clubs" className="py-20 px-4 bg-zinc-900/30 border-t border-border/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">For Club Admins</p>
              <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
                A clear view of your <span className="text-primary">entire club</span>
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Centralize development across all teams. See who is progressing, who needs attention, and where to focus resources — across U8 through Senior, in one operating system.
              </p>

              {/* Stats strip */}
              <div className="mt-6 grid grid-cols-3 gap-3">
                {[
                  { val: '100+', sub: 'Athletes per club' },
                  { val: '6+', sub: 'Teams managed' },
                  { val: '24/7', sub: 'AI insights' },
                ].map((s, i) => (
                  <div key={i} className="bg-zinc-900/40 border border-border/20 rounded-sm p-3">
                    <p className="font-heading text-xl text-primary">{s.val}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.sub}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { icon: Layers, label: 'Multi-team management', sub: 'U8 → Senior, with role-based access' },
                  { icon: Trophy, label: 'Match Reports & history', sub: 'Position-aware scoring + auto-recalc' },
                  { icon: BarChart3, label: 'Performance Intelligence', sub: 'Sortable table, AI insights, gap analysis' },
                  { icon: Brain, label: 'AI Decision Engine', sub: 'Promote · Develop · Focus recommendations' },
                  { icon: Activity, label: 'Global Filters', sub: 'Season · Team · Age · Date range' },
                  { icon: UserCog, label: 'Coaches & Managers', sub: 'Assign roles per team & invite' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                      <item.icon size={15} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm">{item.label}</p>
                      <p className="text-[11px] text-muted-foreground leading-snug">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/club-pilot" className="inline-flex items-center gap-2 mt-8 text-primary text-sm hover:underline uppercase tracking-wider" data-testid="club-section-cta">
                Start a Club Pilot <ArrowRight size={14} />
              </Link>
            </motion.div>

            <ZoomablePreview
              src="/landing/dashboard-preview.png"
              alt="Meybra Club Dashboard — Performance Intelligence"
              caption="Club Dashboard — Meybra Academy"
              accent="primary"
              testId="landing-dashboard-preview"
              onZoom={() => setLightbox({ src: '/landing/dashboard-preview.png', alt: 'Meybra Club Dashboard — Performance Intelligence' })}
            />
          </div>
        </div>
      </section>

      {/* ========== DEVELOPMENT INTELLIGENCE ========== */}
      <section id="development" className="py-20 px-4 border-t border-border/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="order-2 lg:order-1">
              <div className="bg-card border border-border/30 rounded-sm p-6">
                <IntelligenceMockup />
              </div>
            </motion.div>

            <motion.div {...fadeUp} className="order-1 lg:order-2">
              <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">Development Intelligence</p>
              <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
                Development you can <span className="text-primary">actually measure</span>
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Move beyond subjective evaluations. Meybra's intelligence engine tracks training vs. match performance, identifies gaps, and recommends actions.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { icon: BarChart3, label: 'Training vs Match performance comparison' },
                  { icon: Brain, label: 'AI Decision engine (promote, develop, focus)' },
                  { icon: TrendingUp, label: 'Progression tracking over time' },
                  { icon: Eye, label: 'Multiple coach perspectives unified' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
                      <item.icon size={15} className="text-primary" />
                    </div>
                    <span className="text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== ONE PLAYER MULTIPLE PERSPECTIVES ========== */}
      <section className="py-20 px-4 bg-zinc-900/30 border-t border-border/10">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">Differentiator</p>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
              One player, <span className="text-primary">multiple perspectives</span>
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              Meybra unifies insights from every source — creating a complete development picture that no single coach or club can build alone.
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.15 }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
          >
            {[
              { label: 'Private Coach', icon: UserCog, color: 'text-primary' },
              { label: 'Club Coach', icon: Users, color: 'text-emerald-400' },
              { label: 'Match Performance', icon: Target, color: 'text-blue-400' },
              { label: 'Meybra AI', icon: Brain, color: 'text-amber-400' },
              { label: 'Parent Visibility', icon: Eye, color: 'text-purple-400' },
            ].map((p, i) => (
              <div key={i} className="bg-card border border-border/30 rounded-sm p-4 text-center">
                <p.icon size={24} className={`mx-auto mb-3 ${p.color}`} />
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{p.label}</p>
              </div>
            ))}
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.3 }}
            className="mt-8 inline-block"
          >
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <div className="h-px w-8 bg-primary/30" />
              All feeding into one unified player profile
              <div className="h-px w-8 bg-primary/30" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== SCALE SECTION ========== */}
      <section className="py-20 px-4 border-t border-border/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
              Designed for individual development <br className="hidden sm:block" />
              and <span className="text-primary">club scale</span>
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Whether development starts with one private coach or an entire club, Meybra scales with the player.
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.15 }}
            className="mt-12 grid sm:grid-cols-3 gap-5"
          >
            {[
              { num: '1', label: 'Start with a single athlete', sub: 'Private coach or family' },
              { num: '10', label: 'Grow to a full team', sub: 'Add evaluations and matches' },
              { num: '100+', label: 'Scale across a club', sub: 'Multi-team intelligence' },
            ].map((s, i) => (
              <div key={i} className="bg-card border border-border/30 rounded-sm p-6">
                <p className="font-heading text-3xl text-primary">{s.num}</p>
                <p className="text-sm mt-2">{s.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== FOR COACHES ========== */}
      <section className="py-20 px-4 bg-zinc-900/30 border-t border-border/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">For Coaches</p>
              <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
                Structure your <span className="text-primary">coaching methodology</span>
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Whether you are a Private Soccer Coach building your own roster, a Performance Coach specializing in physical development, or part of a Club running multi-team operations, Meybra gives you the tools to evaluate, communicate and develop systematically — and grow your athlete base.
              </p>

              {/* Acquisition model clarification — keeps expectations correct for landing visitors */}
              <p className="mt-3 text-xs text-muted-foreground/80 leading-relaxed">
                <span className="text-primary/80 font-semibold uppercase tracking-wider text-[10px]">Note —</span>{' '}
                Private Soccer Coaches and Performance Coaches sign up directly. Club Coaches are added by their Club Admin through the Club workspace.
              </p>

              {/* Coach Advantage callout */}
              <div className="mt-6 bg-primary/5 border border-primary/20 rounded-sm p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-sm bg-primary/15 flex items-center justify-center shrink-0">
                  <Sparkles size={15} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary">Coach Advantage Program</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Reach 10+ active Athlete Passes and your subscription becomes <span className="text-primary">FREE</span>.
                  </p>
                </div>
              </div>

              <div className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { icon: Users, label: 'Athlete management', sub: 'Profiles, history, position-aware evals' },
                  { icon: FileText, label: 'Training plans builder', sub: 'Structure sessions and progressions' },
                  { icon: Layers, label: 'Drill library (60+)', sub: 'Build your own drill methodology' },
                  { icon: Calendar, label: 'Sessions & calendar', sub: 'Track every session, milestone visible' },
                  { icon: Video, label: 'Video analysis', sub: 'Real match & training footage feedback' },
                  { icon: BarChart3, label: 'Financials dashboard', sub: 'Revenue tracking & vouchers (Pro)' },
                  { icon: MessageSquare, label: 'Parent communication', sub: 'Direct messages and reports' },
                  { icon: Star, label: 'Athlete Pass program', sub: 'Parents pay $9.99/athlete/month' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                      <item.icon size={15} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm">{item.label}</p>
                      <p className="text-[11px] text-muted-foreground leading-snug">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <ZoomablePreview
              src="/landing/coach-dashboard.png"
              alt="Meybra Coach Dashboard — Athletes, Sessions, Drills, Plans"
              caption="Coach Workspace"
              accent="primary"
              testId="landing-coach-preview"
              onZoom={() => setLightbox({ src: '/landing/coach-dashboard.png', alt: 'Meybra Coach Dashboard — Athletes, Sessions, Drills, Plans' })}
            />
          </div>
        </div>
      </section>

      {/* ========== FOR PARENTS ========== */}
      <section className="py-20 px-4 border-t border-border/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ZoomablePreview
              src="/landing/player-detail.png"
              alt="Meybra Athlete Profile — Skills Radar, AI Insights, Development Timeline"
              caption="Athlete Profile — Family View"
              accent="emerald-500"
              wrapperClass="order-2 lg:order-1"
              testId="landing-parent-preview"
              onZoom={() => setLightbox({ src: '/landing/player-detail.png', alt: 'Meybra Athlete Profile — Skills Radar, AI Insights, Development Timeline' })}
            />
            <motion.div {...fadeUp} className="order-1 lg:order-2">
              <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-400/80 mb-3">For Players & Families</p>
              <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
                Don't just see attendance — <br className="hidden sm:block" />
                see <span className="text-emerald-400">real development</span>
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                A complete view of your child's progress — from team coaches, private coaches, and AI insights — unified into one development profile that grows with the player.
              </p>

              {/* Visual highlight */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mb-1">Skills Radar</p>
                  <p className="text-xs text-muted-foreground leading-snug">7 areas tracked: ball ctrl, pass, dribble, finish, speed, balance, decision/awareness</p>
                </div>
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mb-1">AI Insights</p>
                  <p className="text-xs text-muted-foreground leading-snug">Strengths, focus areas and personalized recommendations after every match</p>
                </div>
              </div>

              <div className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { icon: Activity, label: 'Development Profile', sub: 'AI-powered, area scores + timeline' },
                  { icon: Target, label: 'Personalized goals', sub: 'Track milestones with the coach' },
                  { icon: Layers, label: 'Train at Home', sub: 'Guided technical sessions for kids' },
                  { icon: Video, label: 'Video feedback', sub: 'Match clips with coach annotations' },
                  { icon: Calendar, label: 'Sessions calendar', sub: 'Stay aligned with the program' },
                  { icon: FileText, label: 'Monthly progress reports', sub: 'Clear visibility, every month' },
                  { icon: MessageSquare, label: 'Direct messages', sub: 'Talk with coaches in one place' },
                  { icon: Eye, label: 'Multi-coach view', sub: 'Private + Club + AI in one profile' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-sm bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <item.icon size={15} className="text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm">{item.label}</p>
                      <p className="text-[11px] text-muted-foreground leading-snug">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== PRICING / FLEXIBLE PLANS ========== */}
      <section id="pricing" className="py-20 px-4 bg-zinc-900/30 border-t border-border/10">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-4">
              <Sparkles size={11} className="text-primary" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-primary">Founding Beta Access</span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
              Full platform <span className="text-primary">included</span> during the beta
            </h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl mx-auto">
              Early users receive complimentary access while Meybra evolves through real-world validation and feedback. Future pricing shown for transparency.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                kind: 'Private Coach', icon: UserCog, color: 'primary',
                tagline: 'Private Soccer & Performance Coaches — self-serve signup.',
                tiers: [
                  {
                    name: 'Pro', price: '14.99', period: '/month', recommended: true,
                    features: ['Unlimited athletes', 'Training plans + Drill library (60+)', 'Video analysis & feedback', 'White-label branding', 'Financials dashboard', 'Vouchers & packages'],
                  },
                ],
                bonus: 'Coach Advantage: 10+ active Athlete Passes = your subscription stays FREE post-beta.',
                cta: 'Sign up as Soccer Coach', link: '/coach/signup',
                secondaryCta: 'Sign up as Performance Coach', secondaryLink: '/coach/signup?role=performance',
              },
              {
                kind: 'Athlete', icon: Star, color: 'emerald-400',
                tagline: 'Real development visibility for players and families.',
                highlight: true,
                tiers: [
                  {
                    name: 'Athlete Pass', price: '9.99', period: '/athlete/month',
                    features: ['Access training plans', 'Video feedback', 'Progress tracking', 'Direct coach messaging', 'Train at Home library'],
                  },
                  {
                    name: 'Pass Plus', price: '14.99', period: '/athlete/month', recommended: true,
                    features: ['Everything in Athlete Pass', 'AI Development Profile', 'Multi-coach visibility', 'Personalized goals', 'Monthly progress reports'],
                  },
                ],
                bonus: null,
                cta: 'Join Founding Beta', link: '/athlete/signup',
              },
              {
                kind: 'Club', icon: Building2, color: 'blue-400',
                tagline: 'Multi-team operations — Club Admin adds Club Coaches internally.',
                tiers: [
                  {
                    name: 'Per Team', price: '9.99', period: '/team/month', recommended: true,
                    features: ['Unlimited athletes per team', 'Multi-team management', 'Performance Intelligence', 'Match Reports & history', 'AI Decision Engine', 'Coaches & Managers roles'],
                  },
                ],
                bonus: 'Founding club pilots include custom onboarding & dedicated support.',
                cta: 'Request Founding Pilot', link: '/club-pilot',
              },
            ].map((plan, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className={`bg-card border rounded-sm p-6 flex flex-col ${plan.highlight ? 'border-primary/40 shadow-lg shadow-primary/5' : 'border-border/30'}`}
                data-testid={`plan-${plan.kind.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {plan.highlight && (
                  <p className="text-[9px] uppercase tracking-widest text-primary mb-4">Most Popular</p>
                )}
                <div className={`w-10 h-10 rounded-sm bg-${plan.color}/10 border border-${plan.color}/20 flex items-center justify-center mb-4`}>
                  <plan.icon size={18} className={`text-${plan.color}`} />
                </div>
                <h3 className="font-heading text-lg uppercase tracking-wide">{plan.kind} Plans</h3>
                <p className="text-xs text-muted-foreground mt-1 mb-5 leading-relaxed">{plan.tagline}</p>

                {/* Tiers */}
                <div className="space-y-4 flex-1">
                  {plan.tiers.map((tier, ti) => (
                    <div
                      key={ti}
                      className={`rounded-sm p-4 border ${
                        tier.recommended
                          ? `border-${plan.color}/40 bg-${plan.color}/5`
                          : 'border-border/20 bg-zinc-900/30'
                      }`}
                      data-testid={`tier-${plan.kind.toLowerCase().replace(/\s+/g, '-')}-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-baseline justify-between mb-3">
                        <p className="text-[11px] uppercase tracking-widest text-white font-semibold">{tier.name}</p>
                        {tier.recommended && (
                          <span className={`text-[9px] uppercase tracking-widest text-${plan.color} font-semibold`}>Recommended</span>
                        )}
                      </div>
                      {/* Strikethrough price + Founding Beta badge */}
                      <div className="mb-3">
                        <div className="flex items-baseline gap-1 opacity-50">
                          <span className="text-[12px] text-muted-foreground line-through">$</span>
                          <span className="font-heading text-2xl text-muted-foreground line-through decoration-2">{tier.price}</span>
                          <span className="text-[10px] text-muted-foreground line-through">{tier.period}</span>
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 rounded-sm px-2.5 py-1">
                          <Sparkles size={10} className="text-primary" />
                          <span className="text-[10px] uppercase tracking-widest text-primary font-bold">Included during Founding Beta</span>
                        </div>
                      </div>
                      <ul className="space-y-1.5">
                        {tier.features.map((f, fi) => (
                          <li key={fi} className="flex items-start gap-2 text-[12px] text-muted-foreground leading-relaxed">
                            <Check size={11} className={`text-${plan.color} shrink-0 mt-1`} /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {plan.bonus && (
                  <div className={`mt-4 p-3 rounded-sm border bg-${plan.color}/5 border-${plan.color}/15`}>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      <Sparkles size={10} className={`inline mr-1.5 -mt-0.5 text-${plan.color}`} />
                      {plan.bonus}
                    </p>
                  </div>
                )}

                <Link to={plan.link} className="mt-5">
                  <Button className={`w-full ${plan.highlight ? 'bg-primary text-black hover:bg-primary/90' : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-border/30'}`} data-testid={`plan-cta-${plan.kind.toLowerCase().replace(/\s+/g, '-')}`}>
                    {plan.cta} <ArrowRight size={14} className="ml-2" />
                  </Button>
                </Link>
                {plan.secondaryCta && plan.secondaryLink && (
                  <Link to={plan.secondaryLink} className="mt-2">
                    <Button
                      variant="outline"
                      className={`w-full border-${plan.color}/30 text-${plan.color} hover:bg-${plan.color}/10`}
                      data-testid={`plan-cta-${plan.kind.toLowerCase().replace(/\s+/g, '-')}-secondary`}
                    >
                      {plan.secondaryCta} <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>

          {/* Pricing footer note */}
          <p className="text-center mt-8 text-[11px] text-zinc-500">
            All Pro features are currently included during the validation phase · No payment required · Cancel anytime
          </p>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 px-4 border-t border-border/10">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">
              Start where development <span className="text-primary">starts for you</span>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Whether you're a coach, a club, or a family — Meybra meets you where you are.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/get-started">
                <Button className="bg-primary text-black hover:bg-primary/90 px-8 py-3 text-sm font-semibold uppercase tracking-wider h-auto">
                  Join Founding Beta <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/club-pilot">
                <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 px-8 py-3 text-sm font-semibold uppercase tracking-wider h-auto">
                  Request Founding Pilot
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="border-t border-border/10 py-12 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <p className="font-heading text-lg tracking-widest text-primary uppercase">Meybra</p>
            <p className="text-xs text-muted-foreground mt-1">Development Operating System for Youth Soccer</p>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/unified-login" className="hover:text-primary transition-colors">Login</Link>
            <a href="#pricing" className="hover:text-primary transition-colors">Plans</a>
            <Link to="/get-started" className="hover:text-primary transition-colors">Register</Link>
          </div>
        </div>
      </footer>

      {/* ========== LIGHTBOX ========== */}
      {lightbox && (
        <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
};

/* ====== ZOOMABLE PREVIEW + LIGHTBOX ====== */

const ACCENT_MAP = {
  'primary': { glow: 'bg-primary/5', shadow: 'shadow-primary/10' },
  'emerald-500': { glow: 'bg-emerald-500/5', shadow: 'shadow-emerald-500/10' },
};

const ZoomablePreview = ({ src, alt, caption, accent = 'primary', testId, onZoom, wrapperClass = '' }) => {
  const accentClasses = ACCENT_MAP[accent] || ACCENT_MAP.primary;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className={`relative ${wrapperClass}`}
    >
      <div className={`absolute -inset-4 ${accentClasses.glow} blur-3xl rounded-full pointer-events-none`} />
      <button
        type="button"
        onClick={onZoom}
        className={`group relative block w-full bg-card border border-border/30 rounded-sm overflow-hidden shadow-2xl ${accentClasses.shadow} transition-all hover:border-primary/40 cursor-zoom-in text-left`}
        data-testid={`${testId}-trigger`}
        aria-label={`Open ${caption} preview in full size`}
      >
        <div className="flex items-center gap-1.5 bg-zinc-900/80 border-b border-border/30 px-4 py-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          <span className="ml-3 text-[10px] uppercase tracking-widest text-muted-foreground flex-1">{caption}</span>
          <span className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground/70 group-hover:text-primary transition-colors">
            <Maximize2 size={11} /> Zoom
          </span>
        </div>
        <img src={src} alt={alt} className="w-full h-auto block group-hover:opacity-95 transition-opacity" data-testid={testId} />
        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 pointer-events-none">
          <div className="bg-primary text-black px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
            <Maximize2 size={14} /> Click to expand
          </div>
        </div>
      </button>
    </motion.div>
  );
};

const Lightbox = ({ src, alt, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out"
    onClick={onClose}
    data-testid="landing-lightbox"
  >
    <button
      type="button"
      className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-zinc-900/80 border border-border/30 flex items-center justify-center text-white hover:bg-primary hover:text-black transition-colors z-10"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      aria-label="Close preview"
      data-testid="landing-lightbox-close"
    >
      <X size={20} />
    </button>
    <motion.img
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.25 }}
      src={src}
      alt={alt}
      className="max-w-full max-h-full object-contain rounded-sm border border-border/30 shadow-2xl shadow-primary/10"
      onClick={(e) => e.stopPropagation()}
    />
    <p className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-muted-foreground/70">
      Click anywhere or press ESC to close
    </p>
  </motion.div>
);



/* ====== MOCKUP COMPONENTS ====== */

const DashboardMockup = () => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-2 h-2 rounded-full bg-primary" />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Club Dashboard</span>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[{ label: 'Athletes', val: '87' }, { label: 'Teams', val: '6' }, { label: 'Avg Score', val: '7.4' }].map((s, i) => (
        <div key={i} className="bg-zinc-900/60 border border-border/20 rounded-sm p-3 text-center">
          <p className="font-heading text-lg text-primary">{s.val}</p>
          <p className="text-[9px] text-muted-foreground uppercase">{s.label}</p>
        </div>
      ))}
    </div>
    <div className="space-y-2">
      {['U10 Development', 'U12 Elite', 'U14 Premier'].map((t, i) => (
        <div key={i} className="flex items-center gap-3 p-2 bg-zinc-900/40 rounded-sm border border-border/10">
          <span className="text-xs flex-1 text-muted-foreground">{t}</span>
          <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: `${70 + i * 10}%` }} />
          </div>
          <span className="text-xs text-primary font-mono">{(7 + i * 0.3).toFixed(1)}</span>
        </div>
      ))}
    </div>
    <div className="flex items-center gap-2 pt-2">
      <Zap size={12} className="text-amber-400" />
      <span className="text-[10px] text-muted-foreground">3 players flagged for development attention</span>
    </div>
  </div>
);

const IntelligenceMockup = () => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-2 h-2 rounded-full bg-emerald-400" />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Player Intelligence</span>
    </div>
    <div className="flex items-center gap-4 p-3 bg-zinc-900/40 rounded-sm border border-border/10">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">M</div>
      <div className="flex-1">
        <p className="text-sm">Marcus Silva</p>
        <p className="text-[10px] text-muted-foreground">U14 • Attacking Midfielder</p>
      </div>
      <span className="font-heading text-lg text-emerald-400">8.4</span>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="bg-zinc-900/40 border border-border/10 rounded-sm p-3">
        <p className="text-[9px] uppercase text-muted-foreground mb-1">Training Score</p>
        <p className="font-heading text-lg text-primary">8.2</p>
      </div>
      <div className="bg-zinc-900/40 border border-border/10 rounded-sm p-3">
        <p className="text-[9px] uppercase text-muted-foreground mb-1">Match Score</p>
        <p className="font-heading text-lg text-emerald-400">8.6</p>
      </div>
    </div>
    <div className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-sm">
      <div className="flex items-center gap-2">
        <Brain size={12} className="text-emerald-400" />
        <span className="text-[10px] uppercase text-emerald-400">AI Decision</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Ready for promotion — consistent match outperformance</p>
    </div>
  </div>
);

const CoachMockup = () => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-2 h-2 rounded-full bg-primary" />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Coach Workspace</span>
    </div>
    {['Technical: Ball Control', 'Physical: Speed & Agility', 'Tactical: Game Intelligence'].map((area, i) => (
      <div key={i} className="flex items-center gap-3 p-2.5 bg-zinc-900/40 rounded-sm border border-border/10">
        <Target size={14} className="text-primary shrink-0" />
        <span className="text-xs flex-1">{area}</span>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, j) => (
            <div key={j} className={`w-3 h-3 rounded-sm ${j < 3 + i ? 'bg-primary/60' : 'bg-zinc-800'}`} />
          ))}
        </div>
      </div>
    ))}
    <div className="flex items-center gap-2 pt-2 text-[10px] text-muted-foreground">
      <Award size={12} className="text-primary" />
      Structured evaluations shared with parents automatically
    </div>
  </div>
);

const ParentMockup = () => (
  <div className="space-y-3">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-2 h-2 rounded-full bg-emerald-400" />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Parent View</span>
    </div>
    <div className="p-3 bg-zinc-900/40 rounded-sm border border-border/10">
      <p className="text-xs text-muted-foreground mb-2">Latest Feedback</p>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[8px] text-primary">TC</div>
          <span className="text-[10px] text-muted-foreground">Team Coach: "Great improvement in positioning"</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center text-[8px] text-emerald-400">PC</div>
          <span className="text-[10px] text-muted-foreground">Private Coach: "Ball control very solid"</span>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[{ label: 'Overall', val: '7.8', color: 'text-primary' }, { label: 'Progress', val: '+12%', color: 'text-emerald-400' }, { label: 'Matches', val: '14', color: 'text-blue-400' }].map((s, i) => (
        <div key={i} className="bg-zinc-900/40 border border-border/10 rounded-sm p-2 text-center">
          <p className={`font-heading text-sm ${s.color}`}>{s.val}</p>
          <p className="text-[8px] text-muted-foreground uppercase">{s.label}</p>
        </div>
      ))}
    </div>
    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
      <Eye size={12} className="text-emerald-400" />
      Complete development visibility from all sources
    </div>
  </div>
);

export default SaaSLandingPage;
